<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap flex-wrap align-items-center justify-content-between">
          <div>
            <h4 class="mb-3">{{ title }}</h4>
            <p class="mb-0">{{ desc1 }}<br> {{ desc2 }}</p>
          </div>
          <div class="d-flex flex-column">
            <b-button variant="primary pl-5 pr-5" class="mt-2 mr-1" @click="refreshData">데이터 새로고침</b-button>
            <b-form-select v-model="selectedPeriod" :options="periodOptions" class="mt-2 mr-1"></b-form-select>
          </div>
        </div>
      </div>
      <div class="col-lg-12">
        <img v-if="showspinner" style="max-width:100%;" :src="require('@/assets/images/small/spinner.gif')">
      </div>
    </div>

    <!-- 대시보드 섹션 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-md-3">
        <b-card bg-variant="primary" text-variant="white" class="text-center">
          <h5>총 매출(쿠폰제외)</h5>
          <h3>{{ formatCurrency(totalRevenue) }}</h3>
          <p>전월 대비 {{ revenueGrowth > 0 ? '+' : '' }}{{ revenueGrowth }}%</p>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="success" text-variant="white" class="text-center">
          <h5>총 이익</h5>
          <h3>{{ formatCurrency(totalProfit) }}</h3>
          <p>전월 대비 {{ profitGrowth > 0 ? '+' : '' }}{{ profitGrowth }}%</p>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="danger" text-variant="white" class="text-center">
          <h5>총 광고비</h5>
          <h3>{{ formatCurrency(totalAdCost) }}</h3>
          <p>전월 대비 {{ adCostGrowth > 0 ? '+' : '' }}{{ adCostGrowth }}%</p>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="info" text-variant="white" class="text-center">
          <h5>총 판매량</h5>
          <h3>{{ totalSales }}</h3>
          <p>전월 대비 {{ salesGrowth > 0 ? '+' : '' }}{{ salesGrowth }}%</p>
        </b-card>
      </div>
    </div>

    <!-- 판매 추이 차트 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-lg-12">
        <b-card no-body>
          <b-card-header>
            <h5 class="mb-0">판매 추이</h5>
          </b-card-header>
          <b-card-body>
            <ApexChart ref="salesTrendChart" element="sales-trend-chart" :chartOption="salesTrendChartOption" />
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- 상품별 판매 순위 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-lg-12">
        <b-card no-body>
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">상품별 판매 순위</h5>
            <div>
              <b-form-select v-model="productSortBy" :options="productSortOptions" class="mr-2"></b-form-select>
              <b-form-input v-model="productSearch" placeholder="상품명 검색" class="d-inline-block" style="width: 200px;"></b-form-input>
            </div>
          </b-card-header>
          <b-card-body>
            <CDataTable
                storage-key="growthSalesStatus"
              :headers="productTableHeaders"
              :items="filteredProducts"
              :per-page="10"
              :pagination="true"
              itemKey="vendorItemId"
            >
              <template v-slot:상품정보="{ item }">
                <div class="d-flex align-items-center">
                  <img :src="item.썸네일" alt="상품 이미지" style="width: 50px; height: 50px; object-fit: cover;" class="mr-2">
                  <div>
                    <p class="mb-0 font-weight-bold">{{ item.판매상품명 }}</p>
                    <small>{{ item.옵션명 }}</small>
                  </div>
                </div>
              </template>
              <template v-slot:판매추이="{ item }">
                <div style="width: 120px; height: 40px;">
                  <ApexChart :chartOption="getProductTrendChartOption(item)" :element="`product-trend-${item.vendorItemId}`" />
                </div>
              </template>
            </CDataTable>
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- 성과 지표 분석 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-md-6">
        <b-card no-body>
          <b-card-header>
            <h5 class="mb-0">자연 판매 vs 광고 판매</h5>
          </b-card-header>
          <b-card-body>
            <ApexChart ref="salesSourceChart" element="sales-source-chart" :chartOption="salesSourceChartOption" />
          </b-card-body>
        </b-card>
      </div>
      <div class="col-md-6">
        <b-card no-body>
          <b-card-header>
            <h5 class="mb-0">랭킹 및 조회수 추이</h5>
          </b-card-header>
          <b-card-body>
            <ApexChart ref="rankingViewsChart" element="ranking-views-chart" :chartOption="rankingViewsChartOption" />
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ApexChart from '@/components/charts/ApexChart';
import CDataTable from "@/components/common/CDataTable";

export default {
  name: 'growthSalesStatus',
  components: {
    ApexChart,
    CDataTable
  },
  data() {
    return {
      title: "판매현황",
      desc1: "상품별 판매 현황과 추이를 확인하세요",
      desc2: "매출, 이익, 판매량 등의 지표를 분석하여 판매 전략을 수립할 수 있습니다",
      showspinner: false,
      showDashboard: false,

      // 기간 선택
      selectedPeriod: '30days',
      periodOptions: [
        { value: '7days', text: '최근 7일' },
        { value: '30days', text: '최근 30일' },
        { value: '90days', text: '최근 90일' },
        { value: 'thisMonth', text: '이번 달' },
        { value: 'lastMonth', text: '지난 달' },
        { value: 'thisYear', text: '올해' }
      ],

      // 대시보드 데이터
      totalRevenue: 0,
      totalProfit: 0,
      totalSales: 0,
      totalAdCost: 0,
      revenueGrowth: 0,
      profitGrowth: 0,
      salesGrowth: 0,
      adCostGrowth: 0,

      // 판매 추이 차트
      salesTrendChartOption: null,

      // 상품별 판매 데이터
      products: [],
      productSearch: '',
      productSortBy: 'sales',
      productSortOptions: [
        { value: 'sales', text: '판매량 순' },
        { value: 'revenue', text: '매출 순' },
        { value: 'profit', text: '이익 순' },
        { value: 'adCost', text: '광고비 순' }
      ],
      productTableHeaders: [
        { text: '상품정보', value: '상품정보', align: 'left', width: 300, isSlot: true },
        { text: '판매량', value: '판매량', align: 'center', width: 100 },
        { text: '매출', value: '매출', align: 'center', width: 150 },
        { text: '이익', value: '이익', align: 'center', width: 150 },
        { text: '광고비', value: '광고비', align: 'center', width: 150 },
        { text: '판매추이', value: '판매추이', align: 'center', width: 150, isSlot: true }
      ],

      // 성과 지표 차트
      salesSourceChartOption: null,
      rankingViewsChartOption: null,

      // 원본 데이터
      salesData: [],
      dateLabels: []
    };
  },
  computed: {
    filteredProducts() {
      let filtered = [...this.products];

      // 검색어 필터링
      if (this.productSearch) {
        const searchTerm = this.productSearch.toLowerCase();
        filtered = filtered.filter(item =>
          item.판매상품명.toLowerCase().includes(searchTerm) ||
          item.옵션명.toLowerCase().includes(searchTerm)
        );
      }

      // 정렬
      filtered.sort((a, b) => {
        switch (this.productSortBy) {
          case 'sales':
            return b.판매량 - a.판매량;
          case 'revenue':
            return b.매출 - a.매출;
          case 'profit':
            return b.이익 - a.이익;
          case 'conversion':
            return b.전환율 - a.전환율;
          default:
            return b.판매량 - a.판매량;
        }
      });

      return filtered;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.showspinner = true;
      this.showDashboard = false;

      try {
        // 판매 데이터 로드
        const response = await axios.post('/api/sales/dashboard', {
          period: this.selectedPeriod
        });

        if (response.data.success) {
          this.salesData = response.data.salesData;
          this.dateLabels = response.data.dateLabels;

          // 대시보드 데이터 설정
          this.totalRevenue = response.data.totalRevenue;
          this.totalProfit = response.data.totalProfit;
          this.totalSales = response.data.totalSales;
          this.avgConversionRate = response.data.avgConversionRate;
          this.revenueGrowth = response.data.revenueGrowth;
          this.profitGrowth = response.data.profitGrowth;
          this.salesGrowth = response.data.salesGrowth;
          this.conversionGrowth = response.data.conversionGrowth;

          // 상품 데이터 설정
          this.products = response.data.products;

          // 차트 데이터 설정
          this.updateCharts();

          this.showDashboard = true;
        } else {
          // 데이터가 없는 경우 임시 데이터 생성
          this.generateDummyData();
        }
      } catch (error) {
        console.error('데이터 로드 오류:', error);
        // 오류 발생 시 임시 데이터 생성
        this.generateDummyData();
      } finally {
        this.showspinner = false;
      }
    },

    refreshData() {
      this.loadData();
    },

    updateCharts() {
      // 판매 추이 차트 업데이트
      this.salesTrendChartOption = {
        series: [
          {
            name: '매출',
            type: 'column',
            data: this.salesData.map(d => d.revenue)
          },
          {
            name: '이익',
            type: 'column',
            data: this.salesData.map(d => d.profit)
          },
          {
            name: '판매량',
            type: 'line',
            data: this.salesData.map(d => d.sales)
          }
        ],
        chart: {
          height: 350,
          type: 'line',
          stacked: false,
          toolbar: {
            show: true
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: [1, 1, 4]
        },
        xaxis: {
          categories: this.dateLabels
        },
        yaxis: [
          {
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#008FFB'
            },
            labels: {
              style: {
                colors: '#008FFB'
              },
              formatter: (value) => {
                return this.formatCurrency(value);
              }
            },
            title: {
              text: '매출/이익',
              style: {
                color: '#008FFB'
              }
            },
            tooltip: {
              enabled: true
            }
          },
          {
            seriesName: '판매량',
            opposite: true,
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#00E396'
            },
            labels: {
              style: {
                colors: '#00E396'
              }
            },
            title: {
              text: '판매량',
              style: {
                color: '#00E396'
              }
            }
          }
        ],
        tooltip: {
          y: {
            formatter: function (value, { seriesIndex }) {
              if (seriesIndex === 0 || seriesIndex === 1) {
                return '₩' + value.toLocaleString();
              }
              return value;
            }
          }
        },
        legend: {
          position: 'top'
        },
        fill: {
          opacity: 1
        },
        colors: ['#008FFB', '#00E396', '#FEB019']
      };

      // 자연 판매 vs 광고 판매 차트 업데이트
      this.salesSourceChartOption = {
        series: [
          {
            name: '자연 판매',
            data: this.salesData.map(d => Math.round(d.sales * 0.7))
          },
          {
            name: '광고 판매',
            data: this.salesData.map(d => Math.round(d.sales * 0.3))
          }
        ],
        chart: {
          type: 'bar',
          height: 350,
          stacked: true,
          toolbar: {
            show: true
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: this.dateLabels
        },
        yaxis: {
          title: {
            text: '판매량'
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return value + '개';
            }
          }
        },
        colors: ['#00E396', '#008FFB'],
        legend: {
          position: 'top'
        }
      };

      // 랭킹 및 조회수 추이 차트 업데이트
      this.rankingViewsChartOption = {
        series: [
          {
            name: '평균 랭킹',
            type: 'line',
            data: this.salesData.map(() => Math.floor(Math.random() * 30) + 1)
          },
          {
            name: '조회수',
            type: 'column',
            data: this.salesData.map(d => Math.round(d.sales * 50 + Math.random() * 500))
          }
        ],
        chart: {
          height: 350,
          type: 'line',
          stacked: false,
          toolbar: {
            show: true
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: [3, 1]
        },
        xaxis: {
          categories: this.dateLabels
        },
        yaxis: [
          {
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#FF4560'
            },
            labels: {
              style: {
                colors: '#FF4560'
              }
            },
            title: {
              text: '평균 랭킹',
              style: {
                color: '#FF4560'
              }
            },
            tooltip: {
              enabled: true
            },
            reversed: true
          },
          {
            seriesName: '조회수',
            opposite: true,
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#FEB019'
            },
            labels: {
              style: {
                colors: '#FEB019'
              }
            },
            title: {
              text: '조회수',
              style: {
                color: '#FEB019'
              }
            }
          }
        ],
        tooltip: {
          y: {
            formatter: function (value, { seriesIndex }) {
              if (seriesIndex === 0) {
                return value + '위';
              }
              return value + '회';
            }
          }
        },
        legend: {
          position: 'top'
        },
        colors: ['#FF4560', '#FEB019']
      };
    },

    getProductTrendChartOption(product) {
      // 기본 데이터 확인 및 설정
      const salesTrend = product.salesTrend || [0, 0, 0, 0, 0, 0, 0];
      const isPositiveTrend = salesTrend.length > 1 && salesTrend[salesTrend.length - 1] > salesTrend[0];

      return {
        series: [
          {
            name: '판매량',
            data: salesTrend
          }
        ],
        chart: {
          type: 'line',
          height: 40,
          width: 120,
          sparkline: {
            enabled: true
          },
          animations: {
            enabled: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        tooltip: {
          fixed: {
            enabled: false
          },
          x: {
            show: false
          },
          y: {
            title: {
              formatter: function () {
                return '판매량:';
              }
            }
          },
          marker: {
            show: false
          }
        },
        colors: [isPositiveTrend ? '#00E396' : '#FF4560'],
        xaxis: {
          categories: Array.from({ length: salesTrend.length }, (_, i) => i + 1)
        }
      };
    },

    formatCurrency(value) {
      return '₩' + value.toLocaleString();
    },

    generateDummyData() {
      // 임시 데이터 생성 (API 연동 전까지 사용)
      this.totalRevenue = 12500000;
      this.totalProfit = 3750000;
      this.totalSales = 250;
      this.totalAdCost = 1250000;
      this.revenueGrowth = 12.5;
      this.profitGrowth = 8.3;
      this.salesGrowth = 15.2;
      this.adCostGrowth = 5.7;

      // 날짜 라벨 생성
      const today = new Date();
      this.dateLabels = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        this.dateLabels.push(date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }));
      }

      // 판매 데이터 생성
      this.salesData = this.dateLabels.map((date, index) => {
        const baseRevenue = 300000 + Math.random() * 200000;
        const baseProfit = baseRevenue * 0.3;
        const baseSales = 5 + Math.random() * 10;

        return {
          date,
          revenue: Math.round(baseRevenue + (index / 30) * 100000),
          profit: Math.round(baseProfit + (index / 30) * 30000),
          sales: Math.round(baseSales + (index / 30) * 5)
        };
      });

      // 상품 데이터 생성
      this.products = [];
      for (let i = 1; i <= 20; i++) {
        const sales = Math.round(50 + Math.random() * 150);
        const price = Math.round(30000 + Math.random() * 70000);
        const revenue = sales * price;
        const profit = revenue * (0.25 + Math.random() * 0.15);
        const adCost = Math.round(revenue * (0.05 + Math.random() * 0.15));

        // 판매 추이 생성
        const salesTrend = [];
        for (let j = 0; j < 7; j++) {
          salesTrend.push(Math.round(sales * (0.7 + Math.random() * 0.6) / 7));
        }

        this.products.push({
          썸네일: `https://picsum.photos/id/${i + 30}/200/200`,
          판매상품명: `테스트 상품 ${i}`,
          옵션명: `옵션 ${i}`,
          vendorItemId: `VID${i}`,
          productId: `PID${i}`,
          판매량: sales,
          매출: revenue,
          이익: profit,
          광고비: adCost,
          salesTrend
        });
      }

      // 차트 업데이트
      this.updateCharts();
      this.showDashboard = true;
    }
  },
  watch: {
    selectedPeriod() {
      this.loadData();
    }
  }
};
</script>

<style scoped>
.card {
  margin-bottom: 1.5rem;
}
</style>
